import {identity, clamp} from 'lodash';
import {assoc, pipe, emptyArray} from '@packages/utilities/fp/utils';
import {weakMemo, cacheMemo} from '@packages/utilities/memo';
import {insertAt, deleteAt, updateAt, append} from '@packages/utilities/array';

import {
  clampScrollTop as lazyListClampScrollTop,
  bringIndexInView,
} from './lazyList';

import {type Lazy, defer, force} from '@packages/utilities/lazy';

export type ID = string;

export class IDSet implements Iterable<ID> {
  private internalSet: Set<ID>;

  private constructor(data: Set<ID>) {
    this.internalSet = data;
  }

  static from(ids: ID[]): IDSet {
    const set = new Set(ids);
    return new IDSet(set);
  }

  add(id: ID): IDSet {
    if (this.internalSet.has(id)) return this;

    const nextSet = new Set(this.internalSet);
    nextSet.add(id);
    return new IDSet(nextSet);
  }

  delete(id: ID) {
    if (!this.internalSet.has(id)) return this;

    const nextSet = new Set(this.internalSet);
    nextSet.delete(id);
    return new IDSet(nextSet);
  }

  has(id: ID) {
    return this.internalSet.has(id);
  }

  get size(): number {
    return this.internalSet.size;
  }

  /**
   *
   * Implements the Iterable Protocol to define the behaviour when iterating over `IDSet`
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Iteration_protocols
   */
  [Symbol.iterator](): Iterator<ID> {
    return this.internalSet[Symbol.iterator]();
  }
}

export type SortInfo = {
  nodeId: ID;
  anchorId: ID;
  position: 'prepend' | 'append' | 'before' | 'after';
};

export type ScrollViewData = {
  scrollLeft?: number;
  scrollTop?: number;
};

export type ExpandedNodes = Partial<Record<ID, boolean>>;

export type TreeViewRoot<KeyT, NodeT> = {
  getId: IDGetter<NodeT>;
  nodes: TreeViewNodeList<KeyT, NodeT>;
};
/**
 * `NodeT` represents the type of our individual nodes' data.
 *
 * `KeyT` represents the type of the keys we group separate children-lists by.
 *
 * The simplest example for `KeyT` would be that your nodes only have a single
 * list of children.
 *
 * In this case, it doesn't really matter what `KeyT` is, but for clarity you
 * could use the literal string type named after whatever you call the children
 * of such nodes, e.g. `"children"`.
 *
 * You *may* have nodes with *multiple, separate lists of children* in them.
 *
 * For example, suppose your nodes have *two* children-lists, one keyed at
 * "children" and another keyed at "slots". The type for `KeyT` here could be
 * `"children" | "slots"`.
 *
 * Finally, suppose you don't know the *specific* names of the separate
 * children-lists your nodes will have - i.e. they can be named arbitrarily. In
 * this case `KeyT` could just be `string` or some kind of opaque `ID` type.
 */
export type TreeViewNode<KeyT, NodeT> = [
  NodeT,
  Lazy<TreeViewChildren<KeyT, NodeT>>,
];
export type TreeViewNodeList<KeyT, NodeT> = Array<TreeViewNode<KeyT, NodeT>>;

export type TreeViewChildren<KeyT, NodeT> = Array<
  [KeyT, TreeViewNodeList<KeyT, NodeT>]
>;

export type TreeViewState<KeyT, NodeT> = {
  expandedNodes: ExpandedNodes;
  height: number;
  invalidSort: boolean;
  nodeHeight: number;
  potentialParent: null | ID;
  preselectedNode: null | ID;
  root: TreeViewRoot<KeyT, NodeT>;
  scrollView: ScrollViewData;
  selectedNodes: IDSet;
  sort?: null | SortInfo;
  sorting: boolean;
  width: number;
};

export type IDGetter<N> = (arg1: N) => ID;

/**
 * A path to a given `TreeViewNode`, relative to a **ROOT** `TreeViewNodeList`
 *
 * - The `index` parameter refers to the index in the root `TreeViewNodeList`
 * - The remaining `path` refers to the children-key + index path in the
 *   remainder of the tree
 */
type TreeNodePath<KeyT> = {
  index: number;
  path: Array<{
    key: KeyT;
    index: number;
  }>;
};

type NodeInfo<K, N> = {
  parent: null | TreeViewNode<K, N>;
  node: TreeViewNode<K, N>;
  path: TreeNodePath<K>;
};

/**
 * A cache of useful info of TreeViewNodes. Enables fast lookups of the
 * following, given a node ID:
 *
 * - Node data
 * - Parent of node
 * - Path to node
 */
type TreeViewNodeCache<K, N> = Map<ID, NodeInfo<K, N>>;

type DebugTree<K> = {
  id: ID;
  // @ts-expect-error - TS2344 - Type 'K' does not satisfy the constraint 'string | number | symbol'.
  kids: Partial<Record<K, Array<DebugTree<K>>>>;
};

const debugTree_ =
  <K, N>(getId: IDGetter<N>): ((arg1: TreeViewNode<K, N>) => DebugTree<K>) =>
  (treeViewNode: TreeViewNode<K, N>): DebugTree<K> => {
    return {
      id: getId(treeViewNode[0]),
      // @ts-expect-error - TS2322 - Type 'Record<string, any>' is not assignable to type 'Partial<Record<K, DebugTree<K>[]>>'.
      kids: force(treeViewNode[1]).reduce<Record<string, any>>(
        (result, [childrenKey, nodes]: [any, any]) => {
          result[childrenKey] = nodes.map(debugTree_(getId));
          return result;
        },
        {}
      ),
    };
  };

/**
 * Useful for quickly debugging a TreeViewRoot's high-level structure.
 */
const debugTree = <K, N>(
  treeViewRoot: TreeViewRoot<K, N>
): Array<DebugTree<K>> => {
  return treeViewRoot.nodes.map(debugTree_(treeViewRoot.getId));
};

/**
 * Gets the `NodeInfo` for a given node by ID. Attempts to harness the existing
 * cache produced from `flatten`, and if that fails, will walk the tree until it
 * gathers all the info it needs, populating the cache with info of every node
 * we see.
 *
 * Performance note: This could be much faster. We re-walk parts of the tree
 * we've already walked if we miss the cache, which is particularly wasteful in
 * cases where the node we're looking for is near the end of the depth-first
 * order of the tree.
 *
 * Could we instead "resume" wherever we were within the deepest walk thus far?
 * Would we need to refactor this to be iterative for this to be possible, and
 * is that even possible without manually tracking a stack of parameters?
 * Probably true for the former, and false for the latter. The words "tail call
 * recursion" fade into the hazy CS portion of my brain.
 */
const getNodeInfo = weakMemo(
  <K, N>(
    expandedNodes: ExpandedNodes
  ): ((arg1: TreeViewRoot<K, N>) => (arg1: ID) => undefined | NodeInfo<K, N>) =>
    weakMemo((treeRoot: TreeViewRoot<K, N>) => {
      const cache: TreeViewNodeCache<K, N> = getCacheFromFlatten(
        expandedNodes,
        treeRoot
      );

      const findNodeInfo = (
        tree: TreeViewNodeList<K, N>,
        id: ID,
        iteration: null | {
          key: K;
          parent: TreeViewNode<K, N>;
          path: TreeNodePath<K>;
        }
      ): undefined | NodeInfo<K, N> => {
        if (cache.has(id)) {
          return cache.get(id);
        }

        for (let parentIdx = 0; parentIdx < tree.length; parentIdx += 1) {
          const parent = tree[parentIdx];
          const parentPath: TreeNodePath<K> = !iteration
            ? {
                index: parentIdx,
                path: emptyArray,
              }
            : {
                index: iteration.path.index,
                path: [
                  ...iteration.path.path,
                  {key: iteration.key, index: parentIdx},
                ],
              };

          const nodeInfo: NodeInfo<K, N> = {
            // @ts-expect-error - TS2322 - Type 'TreeViewNode<K, N> | undefined' is not assignable to type 'TreeViewNode<K, N>'.
            node: parent,
            parent: !iteration ? null : iteration.parent,
            path: parentPath,
          };

          // @ts-expect-error - TS18048 - 'parent' is possibly 'undefined'.
          const parentId = treeRoot.getId(parent[0]);
          cache.set(parentId, nodeInfo);

          if (parentId === id) {
            return nodeInfo;
          }

          // @ts-expect-error - TS18048 - 'parent' is possibly 'undefined'.
          const children = force(parent[1]);
          for (const [nextKey, childNodes] of children) {
            const childResult = findNodeInfo(childNodes, id, {
              // @ts-expect-error - TS2322 - Type 'TreeViewNode<K, N> | undefined' is not assignable to type 'TreeViewNode<K, N>'.
              parent,
              key: nextKey,
              path: parentPath,
            });

            if (childResult) {
              return childResult;
            }
          }
        }
      };

      return (id: ID) => findNodeInfo(treeRoot.nodes, id, null);
    })
);

const getIn_ = <K, N>(
  treeViewNode: TreeViewNode<K, N>,
  subPath: TreeNodePath<K>['path']
): undefined | TreeViewNode<K, N> => {
  if (subPath.length === 0) {
    return treeViewNode;
  }

  // @ts-expect-error - TS2339 - Property 'key' does not exist on type '{ key: K; index: number; } | undefined'. | TS2339 - Property 'index' does not exist on type '{ key: K; index: number; } | undefined'.
  const [{key, index}, ...nextSubPath] = subPath;

  const nextTreeViewChildrenEntry = force(treeViewNode[1]).find(
    // @ts-expect-error - TS2769 - No overload matches this call.
    ([k]: [any]) => k === key
  );
  if (!nextTreeViewChildrenEntry) {
    return undefined;
  }

  const nextTreeViewNode = nextTreeViewChildrenEntry[1][index];
  if (!nextTreeViewNode) {
    return undefined;
  }

  return getIn_(nextTreeViewNode, nextSubPath);
};

const getIn = <K, N>(
  tree: TreeViewNodeList<K, N>,
  path: TreeNodePath<K>
): undefined | TreeViewNode<K, N> => {
  const nextTreeViewNode = tree[path.index];
  if (!nextTreeViewNode) {
    return undefined;
  }

  return getIn_(nextTreeViewNode, path.path);
};

const set = <T>(arr: Array<T>, index: number, val: T): Array<T> =>
  updateAt(arr, index, () => val);

const removeIn_ = <K, N>(
  treeViewNode: TreeViewNode<K, N>,
  subPath: TreeNodePath<K>['path']
): undefined | TreeViewNode<K, N> => {
  if (subPath.length === 0) {
    return undefined;
  }

  // @ts-expect-error - TS2339 - Property 'key' does not exist on type '{ key: K; index: number; } | undefined'. | TS2339 - Property 'index' does not exist on type '{ key: K; index: number; } | undefined'.
  const [{key, index}, ...nextSubPath] = subPath;

  // Perf tradeoff: `removeIn` always produces a new tree, but is lazy about
  // deep tree walks:
  return [
    treeViewNode[0],
    defer(() => {
      const childrenMap = force(treeViewNode[1]);
      const childrenListIndex = childrenMap.findIndex(
        // @ts-expect-error - TS2345 - Argument of type '([k]: [any]) => boolean' is not assignable to parameter of type '(value: [K, TreeViewNodeList<K, N>], index: number, obj: [K, TreeViewNodeList<K, N>][]) => unknown'.
        ([k]: [any]) => k === key
      );
      const childrenEntry = childrenMap[childrenListIndex];
      if (!childrenEntry) {
        return childrenMap;
      }
      const treeViewNodeAtIndex = childrenEntry[1][index];
      if (!treeViewNodeAtIndex) {
        return childrenMap;
      }
      const updatedTreeViewNodeAtIndex = removeIn_(
        treeViewNodeAtIndex,
        nextSubPath
      );
      return set(childrenMap, childrenListIndex, [
        childrenEntry[0],
        updatedTreeViewNodeAtIndex
          ? set(childrenEntry[1], index, updatedTreeViewNodeAtIndex)
          : deleteAt(childrenEntry[1], index),
      ]);
    }),
  ];
};

const removeIn = <K, N>(
  tree: TreeViewNodeList<K, N>,
  path: TreeNodePath<K>
): TreeViewNodeList<K, N> => {
  const nextTreeViewNode = tree[path.index];
  if (!nextTreeViewNode) {
    return tree;
  }

  const updatedTreeViewNode = removeIn_(nextTreeViewNode, path.path);
  if (updatedTreeViewNode === nextTreeViewNode) {
    return tree;
  }

  if (!updatedTreeViewNode) {
    return deleteAt(tree, path.index);
  }

  return set(tree, path.index, updatedTreeViewNode);
};

const updateIn_ = <K, N>(
  treeViewNode: TreeViewNode<K, N>,
  subPath: TreeNodePath<K>['path'],
  updater: (arg1: TreeViewNode<K, N>) => TreeViewNode<K, N>
): TreeViewNode<K, N> => {
  if (subPath.length === 0) {
    return updater(treeViewNode);
  }

  // @ts-expect-error - TS2339 - Property 'key' does not exist on type '{ key: K; index: number; } | undefined'. | TS2339 - Property 'index' does not exist on type '{ key: K; index: number; } | undefined'.
  const [{key, index}, ...nextSubPath] = subPath;

  const childrenMap = force(treeViewNode[1]);
  // @ts-expect-error - TS2345 - Argument of type '([k]: [any]) => boolean' is not assignable to parameter of type '(value: [K, TreeViewNodeList<K, N>], index: number, obj: [K, TreeViewNodeList<K, N>][]) => unknown'.
  const childrenListIndex = childrenMap.findIndex(([k]: [any]) => k === key);
  const childrenEntry = childrenMap[childrenListIndex];
  if (!childrenEntry) {
    return treeViewNode;
  }

  return [
    treeViewNode[0],
    defer(() =>
      set(childrenMap, childrenListIndex, [
        childrenEntry[0],
        updateAt(childrenEntry[1], index, (nextTreeViewNode) =>
          updateIn_(nextTreeViewNode, nextSubPath, updater)
        ),
      ])
    ),
  ];
};

const updateChildrenIn = <K, N>(
  tree: TreeViewNodeList<K, N>,
  [path, childrenKey]: [any, any],
  updater: (arg1: TreeViewNodeList<K, N>) => TreeViewNodeList<K, N>
): TreeViewNodeList<K, N> => {
  const nextTreeViewNode = tree[path.index];
  if (!nextTreeViewNode) {
    return tree;
  }

  const updatedTreeViewNode = updateIn_(
    nextTreeViewNode,
    path.path,
    (treeViewNode) => {
      const childrenMap = force(treeViewNode[1]);
      const childrenListIndex = childrenMap.findIndex(
        // @ts-expect-error - TS2345 - Argument of type '([k]: [any]) => boolean' is not assignable to parameter of type '(value: [K, TreeViewNodeList<K, N>], index: number, obj: [K, TreeViewNodeList<K, N>][]) => unknown'.
        ([k]: [any]) => k === childrenKey
      );
      const childrenEntry = childrenMap[childrenListIndex];
      if (!childrenEntry) {
        return treeViewNode;
      }

      return [
        treeViewNode[0],
        defer(() =>
          set(childrenMap, childrenListIndex, [
            childrenKey,
            updater(childrenEntry[1]),
          ])
        ),
      ];
    }
  );
  if (updatedTreeViewNode === nextTreeViewNode) {
    return tree;
  }

  return set(tree, path.index, updatedTreeViewNode);
};

const getParent = <K, N>(
  tree: TreeViewRoot<K, N>,
  id: ID,
  expandedNodes: ExpandedNodes
): null | TreeViewNode<K, N> => {
  // @ts-expect-error - TS2322 - Type 'TreeViewNode<unknown, unknown> | null' is not assignable to type 'TreeViewNode<K, N> | null'. | TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
  return getNodeInfo(expandedNodes)(tree)(id)?.parent || null;
};

const memoizeNodeId = cacheMemo(100);
const __findIndexById = weakMemo(
  <K, N>(
    getId: IDGetter<N>
  ): ((arg1: ID) => (arg1: TreeViewNodeList<K, N>) => undefined | number) =>
    memoizeNodeId(
      (id: ID): ((arg1: TreeViewNodeList<K, N>) => undefined | number) =>
        weakMemo((nodes: TreeViewNodeList<K, N>): undefined | number => {
          const idx = nodes.findIndex((node) => getId(node[0]) === id);
          if (idx < 0) {
            return undefined;
          }
          return idx;
        })
    )
);

const findIndexById = <K, N>(
  getId: IDGetter<N>,
  id: ID,
  nodes: TreeViewNodeList<K, N>
) => __findIndexById(getId)(id)(nodes);

const getPathToParentChildren = <K, N>(
  tree: TreeViewRoot<K, N>,
  id: ID,
  expandedNodes: ExpandedNodes
): null | [TreeNodePath<K>, K] => {
  // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
  const nodeInfo = getNodeInfo(expandedNodes)(tree)(id);
  const parent = nodeInfo?.parent;

  if (parent === undefined) {
    throw new Error(`Could not find parent of node with ID ${id}`);
  }

  if (parent === null) {
    return null;
  }
  // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
  const parentNodeInfo = getNodeInfo(expandedNodes)(tree)(
    // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'N'.
    tree.getId(parent[0])
  );
  if (!parentNodeInfo) {
    throw new Error(
      `Could not find info for parent node with ID ${tree.getId(
        // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'N'.
        parent[0]
      )} (parent of node with ID ${id})`
    );
  }
  const parentPath = parentNodeInfo?.path;
  const childrenList = force(parentNodeInfo.node[1])[0];
  if (!childrenList) {
    throw new Error(
      `Parent node with ID ${id} (parent of node with ID ${id}) has no children lists - how?`
    );
  }
  return [
    // @ts-expect-error - TS2322 - Type 'TreeNodePath<unknown>' is not assignable to type 'TreeNodePath<K>'.
    parentPath,
    // FIXME: This always points to the first children-list in the
    // TreeViewChildren, but we will need a way to select which children-list
    // we want to insert into. Should that be a parameter passed into this
    // function? Should it be of type `K` or should it just be the index into
    // the TreeViewChildren? Oof.
    // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'K'.
    childrenList[0],
  ];
};

const getPathToChildren = <K, N>(
  tree: TreeViewRoot<K, N>,
  id: ID,
  expandedNodes: ExpandedNodes
): null | [TreeNodePath<K>, K] => {
  // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
  const nodeInfo = getNodeInfo(expandedNodes)(tree)(id);
  if (nodeInfo === undefined) {
    throw new Error(`Could not find node with ID ${id}`);
  }
  const childrenList = force(nodeInfo.node[1])[0];
  if (!childrenList) {
    throw new Error(`Node with ID ${id} has no children lists`);
  }
  return [
    // @ts-expect-error - TS2322 - Type 'TreeNodePath<unknown>' is not assignable to type 'TreeNodePath<K>'.
    nodeInfo.path,
    // FIXME: This always points to the first children-list in the
    // TreeViewChildren, but we will need a way to select which children-list
    // we want to insert into. Should that be a parameter passed into this
    // function? Should it be of type `K` or should it just be the index into
    // the TreeViewChildren? Oof.
    // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'K'.
    childrenList[0],
  ];
};

const findTreeNode = <K, N>(
  tree: TreeViewRoot<K, N>,
  id: ID,
  expandedNodes: ExpandedNodes
): null | TreeViewNode<K, N> => {
  // @ts-expect-error - TS2322 - Type 'TreeViewNode<unknown, unknown> | null' is not assignable to type 'TreeViewNode<K, N> | null'. | TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
  return getNodeInfo(expandedNodes)(tree)(id)?.node || null;
};

const addNodeAtPosition = {
  before: <K, N>(
    getId: IDGetter<N>,
    node: TreeViewNode<K, N>,
    anchorId: ID,
    expandedNodes: ExpandedNodes
  ): ((arg1: TreeViewRoot<K, N>) => TreeViewRoot<K, N>) => {
    return (tree: TreeViewRoot<K, N>) => {
      const updatedNodes = updateChildrenIn(
        tree.nodes,
        // @ts-expect-error - `getPathToParentChildren` may return `null`
        getPathToParentChildren(tree, anchorId, expandedNodes),
        (children) => {
          const index = findIndexById(getId, anchorId, children);
          if (index === undefined) {
            throw new Error(
              `Could not determine index of anchor node with ID ${anchorId}`
            );
          }
          return insertAt(children, index, node);
        }
      );

      return {
        getId: tree.getId,
        nodes: updatedNodes,
      };
    };
  },

  after: <K, N>(
    getId: IDGetter<N>,
    node: TreeViewNode<K, N>,
    anchorId: ID,
    expandedNodes: ExpandedNodes
  ): ((arg1: TreeViewRoot<K, N>) => TreeViewRoot<K, N>) => {
    return (tree: TreeViewRoot<K, N>) => {
      const pathToParentChildren = getPathToParentChildren(
        tree,
        anchorId,
        expandedNodes
      );
      if (!pathToParentChildren) {
        throw new Error(
          `Could not determine path to parent children of anchor node with ID ${anchorId}`
        );
      }

      const updatedNodes = updateChildrenIn(
        tree.nodes,
        pathToParentChildren,
        (children) => {
          const index = findIndexById(getId, anchorId, children);
          if (index === undefined) {
            throw new Error(
              `Could not determine index of anchor node with ID ${anchorId}`
            );
          }
          return insertAt(children, index + 1, node);
        }
      );

      return {
        getId: tree.getId,
        nodes: updatedNodes,
      };
    };
  },

  prepend: <K, N>(
    getId: IDGetter<N>,
    node: TreeViewNode<K, N>,
    anchorId: ID,
    expandedNodes: ExpandedNodes
  ): ((arg1: TreeViewRoot<K, N>) => TreeViewRoot<K, N>) => {
    return (tree: TreeViewRoot<K, N>) => {
      const pathToChildren = getPathToChildren(tree, anchorId, expandedNodes);
      if (!pathToChildren) {
        throw new Error(
          `Could not determine path to children of anchor node with ID ${anchorId}`
        );
      }

      const updatedNodes = updateChildrenIn(
        tree.nodes,
        pathToChildren,
        (tree_) => insertAt(tree_, 0, node)
      );

      return {
        getId: tree.getId,
        nodes: updatedNodes,
      };
    };
  },

  append: <K, N>(
    getId: IDGetter<N>,
    node: TreeViewNode<K, N>,
    anchorId: ID,
    expandedNodes: ExpandedNodes
  ): ((arg1: TreeViewRoot<K, N>) => TreeViewRoot<K, N>) => {
    return (tree: TreeViewRoot<K, N>) => {
      const pathToChildren = getPathToChildren(tree, anchorId, expandedNodes);
      if (!pathToChildren) {
        throw new Error(
          `Could not determine path to children of anchor node with ID ${anchorId}`
        );
      }

      const updatedNodes = updateChildrenIn(
        tree.nodes,
        pathToChildren,
        (tree_) => append(tree_, node)
      );

      return {
        getId: tree.getId,
        nodes: updatedNodes,
      };
    };
  },
} as const;

const addNodeAtAnchor = <K, N>(
  getId: IDGetter<N>,
  node: TreeViewNode<K, N>,
  anchorId: ID,
  position: SortInfo['position'],
  expandedNodes: ExpandedNodes
): ((arg1: TreeViewRoot<K, N>) => TreeViewRoot<K, N>) => {
  return addNodeAtPosition[position](getId, node, anchorId, expandedNodes);
};

const moveNode = weakMemo(
  <K, N>(
    expandedNodes: ExpandedNodes
  ): ((arg1: SortInfo) => (arg1: TreeViewRoot<K, N>) => TreeViewRoot<K, N>) =>
    weakMemo(
      ({
        nodeId,
        anchorId,
        position,
      }: SortInfo): ((arg1: TreeViewRoot<K, N>) => TreeViewRoot<K, N>) =>
        weakMemo((root: TreeViewRoot<K, N>): TreeViewRoot<K, N> => {
          if (nodeId === anchorId) {
            return root;
          }
          // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
          const nodeInfo = getNodeInfo(expandedNodes)(root)(nodeId);
          if (!nodeInfo) {
            throw new Error(
              `Failed to retreive tree view node info for node with ID ${nodeId}`
            );
          }

          const {node: treeViewNode, path} = nodeInfo;
          const updatedTree = removeIn(root.nodes, path);

          // @ts-expect-error - TS2322 - Type 'TreeViewRoot<unknown, N>' is not assignable to type 'TreeViewRoot<K, N>'.
          return addNodeAtAnchor(
            root.getId,
            // @ts-expect-error - TS2345 - Argument of type 'TreeViewNode<unknown, unknown>' is not assignable to parameter of type 'TreeViewNode<unknown, N>'.
            treeViewNode,
            anchorId,
            position,
            expandedNodes
          )({getId: root.getId, nodes: updatedTree});
        })
    )
);

export {getParent, findTreeNode, moveNode};

export const flagNodes = weakMemo(<K, N>(root: TreeViewRoot<K, N>) =>
  weakMemo((expandedNodes: ExpandedNodes) => {
    const {getId, nodes} = root;
    const result = {...expandedNodes} as const;
    // @ts-expect-error - TS2345 - Argument of type '([node]: [any]) => void' is not assignable to parameter of type '(value: TreeViewNode<K, N>, index: number, array: TreeViewNode<K, N>[]) => void'.
    nodes.forEach(([node]: [any]) => {
      // @ts-expect-error - TS2542 - Index signature in type '{ readonly [x: string]: boolean | undefined; }' only permits reading.
      result[getId(node)] = true;
    });
    return result;
  })
);

const flatten_ = <K, N>(
  getId: IDGetter<N>,
  nodes: TreeViewNodeList<K, N>,
  expandedNodes: ExpandedNodes,
  iteration: null | {
    key: K;
    parent: TreeViewNode<K, N>;
    path: TreeNodePath<K>;
  },
  cache: TreeViewNodeCache<K, N>
): TreeViewNodeList<K, N> => {
  const result: TreeViewNodeList<K, N> = [];
  nodes.forEach((treeViewNode, parentIdx) => {
    result.push(treeViewNode);
    const [node, lazyChildren] = treeViewNode;
    const children = force(lazyChildren);

    const parentPath: TreeNodePath<K> = !iteration
      ? {
          index: parentIdx,
          path: emptyArray,
        }
      : {
          index: iteration.path.index,
          path: [
            ...iteration.path.path,
            {key: iteration.key, index: parentIdx},
          ],
        };

    cache.set(getId(node), {
      node: treeViewNode,
      parent: iteration ? iteration.parent : null,
      path: parentPath,
    });

    if (hasChildren(children) && expandedNodes[getId(node)]) {
      children.forEach(([key, nextRoot]: [any, any]) => {
        flatten_(
          getId,
          nextRoot,
          expandedNodes,
          {
            key,
            parent: treeViewNode,
            path: parentPath,
          },
          cache // This will mutate!
        ).forEach((x) => {
          result.push(x);
        });
      });
    }
  });
  return result;
};

const flatten = weakMemo(
  <K, N>(
    expandedNodes: ExpandedNodes
  ): ((arg1: TreeViewRoot<K, N>) => NodeListResult<K, N>) =>
    weakMemo((root: TreeViewRoot<K, N>): NodeListResult<K, N> => {
      const cache = new Map();
      return [
        flatten_(root.getId, root.nodes, expandedNodes, null, cache),
        cache,
      ];
    })
);

/**
 * When we flatten our node tree, we create an index of useful info about each
 * node we encounter and return it along with the flat list of `TreeViewNode`s.
 *
 * In practice, this saves us a lot of computation as we rarely need to look up
 * anything outside the flat list of visible nodes in the tree.
 */
type NodeListResult<K, N> = [TreeViewNodeList<K, N>, TreeViewNodeCache<K, N>];

export const nodeList = <K, N>(
  expandedNodes: ExpandedNodes,
  root: TreeViewRoot<K, N>
  // @ts-expect-error - TS2322 - Type 'TreeViewNodeList<unknown, unknown>' is not assignable to type 'TreeViewNodeList<K, N>'. | TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
): TreeViewNodeList<K, N> => flatten(expandedNodes)(root)[0];

/**
 * Get the partial index created while walking the partially-expanded tree.
 */
const getCacheFromFlatten = <K, N>(
  expandedNodes: ExpandedNodes,
  root: TreeViewRoot<K, N>
  // @ts-expect-error - TS2322 - Type 'TreeViewNodeCache<unknown, unknown>' is not assignable to type 'TreeViewNodeCache<K, N>'. | TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
): TreeViewNodeCache<K, N> => flatten(expandedNodes)(root)[1];

// Wow, it sure is a pain to add types to generic functions.
//
// To get around many `N is incompatible with N due to use of * or lack of type
// annotations` errors, you must explicitly annotate the _return values_ of each
// curried function, which means tons of redundancy.
//
// This strikes me as a bug with Flow.
//
// Note: This is NOT related to using `weakMemo` directly, but a problem with
// curried functions in general; in other words, `weakMemo` is only problematic
// insofar as it *tends to lead to more curried functions*.
const parentNode = weakMemo(
  <K, N>(
    expandedNodes: ExpandedNodes
  ): ((arg1: TreeViewRoot<K, N>) => (arg1: N) => null | N) =>
    weakMemo((root: TreeViewRoot<K, N>) =>
      weakMemo((node: N) => {
        const parentTreeViewNode = getParent(
          root,
          root.getId(node),
          expandedNodes
        );
        return parentTreeViewNode ? parentTreeViewNode[0] : null;
      })
    )
);

export const nodeDepth = weakMemo(
  <K, N>(
    expandedNodes: ExpandedNodes
  ): ((arg1: TreeViewRoot<K, N>) => (arg1: N) => number) =>
    weakMemo((root: TreeViewRoot<K, N>): ((arg1: N) => number) =>
      weakMemo((node: N): number => {
        // @ts-expect-error - TS2345 - Argument of type '([nestedNode]: [any]) => boolean' is not assignable to parameter of type '(value: TreeViewNode<K, N>, index: number, array: TreeViewNode<K, N>[]) => unknown'.
        if (root.nodes.some(([nestedNode]: [any]) => nestedNode === node)) {
          return 0;
        }
        // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
        const parent = parentNode(expandedNodes)(root)(node);

        if (!parent) {
          throw new Error(
            `Could not get parent node from node with ID ${root.getId(node)}`
          );
        }

        // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
        return nodeDepth(expandedNodes)(root)(parent) + 1;
      })
    )
);

const parentAtDepth = <K, N>(
  node: N,
  depth: number,
  root: TreeViewRoot<K, N>,
  expandedNodes: ExpandedNodes
): N => {
  // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'N'. | TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
  return nodeDepth(expandedNodes)(root)(node) === depth
    ? node
    : // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'N'.
      parentAtDepth(
        // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
        parentNode(expandedNodes)(root)(node),
        depth,
        // @ts-expect-error - parentNode can return null which is incompatible with N
        root,
        expandedNodes
      );
};

export const getAnchoredPosition = <K, N>(
  id: ID,
  expandedNodes: ExpandedNodes,
  root: TreeViewRoot<K, N>
): SortInfo => {
  const treeNode = findTreeNode(root, id, expandedNodes);
  if (!treeNode) {
    throw new Error(`Could not find node with id ${id}`);
  }
  const node: N = treeNode[0];
  const list = nodeList(expandedNodes, root);
  // @ts-expect-error - TS2345 - Argument of type '([n]: [any]) => boolean' is not assignable to parameter of type '(value: TreeViewNode<K, N>, index: number, obj: TreeViewNode<K, N>[]) => unknown'.
  const index = list.findIndex(([n]: [any]) => n === node);
  // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
  const depth = nodeDepth(expandedNodes)(root)(node);

  const treeNodeAfter = list[index + 1];
  if (
    !treeNodeAfter ||
    // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
    nodeDepth(expandedNodes)(root)(treeNodeAfter[0]) > depth
  ) {
    const nodeTreeBefore = list[index - 1];
    if (!nodeTreeBefore) {
      throw new Error(`Could not get Tree`);
    }
    const [nodeBefore] = nodeTreeBefore;
    // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
    const nodeBeforeDepth = nodeDepth(expandedNodes)(root)(nodeBefore);

    if (nodeBeforeDepth === depth) {
      return {
        nodeId: id,
        anchorId: root.getId(nodeBefore),
        position: 'after',
      };
    }

    if (nodeBeforeDepth < depth) {
      return {
        nodeId: id,
        anchorId: root.getId(nodeBefore),
        position: 'prepend',
      };
    }

    const anchor = parentAtDepth(nodeBefore, depth, root, expandedNodes);

    return {
      nodeId: id,
      anchorId: root.getId(anchor),
      position: 'after',
    };
  }

  // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
  const isLastChild = nodeDepth(expandedNodes)(root)(treeNodeAfter[0]) < depth;

  if (isLastChild) {
    return {
      nodeId: id,
      anchorId: root.getId(
        // @ts-expect-error `getParent` could produce `null`
        getParent(root, id, expandedNodes)[0]
      ),
      position: 'append',
    };
  }

  return {
    nodeId: id,
    anchorId: root.getId(treeNodeAfter[0]),
    position: 'before',
  };
};

export const isLastChildPosition = <K, N>(
  id: ID,
  expandedNodes: ExpandedNodes,
  root: TreeViewRoot<K, N>
): boolean => {
  const treeNode = findTreeNode(root, id, expandedNodes);
  if (!treeNode) {
    throw new Error(`Could not find node with id ${id}`);
  }
  const node: N = treeNode[0];

  // @ts-expect-error - TS2345 - Argument of type '([rootNode]: [any]) => boolean' is not assignable to parameter of type '(value: TreeViewNode<K, N>, index: number, array: TreeViewNode<K, N>[]) => unknown'.
  if (root.nodes.some(([rootNode]: [any]) => rootNode === node)) {
    return false;
  }

  const list = nodeList(expandedNodes, root);
  // @ts-expect-error - TS2345 - Argument of type '([n]: [any]) => boolean' is not assignable to parameter of type '(value: TreeViewNode<K, N>, index: number, obj: TreeViewNode<K, N>[]) => unknown'.
  const index = list.findIndex(([n]: [any]) => n === node);
  // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
  const depth = nodeDepth(expandedNodes)(root)(node);
  // @ts-expect-error - TS2322 - Type 'TreeViewNode<K, N> | undefined' is not assignable to type 'TreeViewNode<K, N>'.
  const prevTreeNode: TreeViewNode<K, N> = list[index - 1];
  if (!prevTreeNode) {
    throw new Error(`Could not get TreeNode above node with ID ${id}`);
  }
  const nextTreeNode = list[index + 1];

  if (!nextTreeNode) {
    return Boolean(depth);
  }
  const [prevNode, prevNodeChildren] = prevTreeNode;
  const [nextNode] = nextTreeNode;

  // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
  const prevNodeDepth = nodeDepth(expandedNodes)(root)(prevNode);

  if (
    prevNodeDepth > depth ||
    // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
    nodeDepth(expandedNodes)(root)(nextNode) < depth ||
    (expandedNodes[root.getId(prevNode)] &&
      prevNodeDepth === depth &&
      !hasChildren(force(prevNodeChildren)))
  ) {
    return true;
  }

  return false;
};

export const getNextAnchoredPosition = <K, N>(
  id: ID,
  expandedNodes: ExpandedNodes,
  root: TreeViewRoot<K, N>
): null | SortInfo => {
  const list = nodeList(expandedNodes, root);
  const treeNode = findTreeNode(root, id, expandedNodes);
  if (!treeNode) {
    throw new Error(`Could not find node with id ${id}`);
  }
  const node: N = treeNode[0];

  // @ts-expect-error - TS2345 - Argument of type '([rootNode]: [any]) => boolean' is not assignable to parameter of type '(value: TreeViewNode<K, N>, index: number, array: TreeViewNode<K, N>[]) => unknown'.
  if (root.nodes.some(([rootNode]: [any]) => rootNode === node)) {
    return null;
  }

  // @ts-expect-error - TS2345 - Argument of type '([n]: [any]) => boolean' is not assignable to parameter of type '(value: TreeViewNode<K, N>, index: number, obj: TreeViewNode<K, N>[]) => unknown'.
  const index = list.findIndex(([n]: [any]) => n === node);
  // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
  const depth = nodeDepth(expandedNodes)(root)(node);
  const prevTreeNode = list[index - 1];
  if (!prevTreeNode) {
    throw new Error(`Could not get TreeNode above node with ID ${id}`);
  }
  const [prevNode] = prevTreeNode;
  // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
  const prevNodeDepth = nodeDepth(expandedNodes)(root)(prevNode);

  if (prevNodeDepth < depth) {
    return null;
  }

  if (prevNodeDepth === depth) {
    if (expandedNodes[root.getId(prevNode)]) {
      return {
        nodeId: id,
        anchorId: root.getId(prevNode),
        position: 'append',
      };
    }
    return null;
  }

  const anchor = parentAtDepth(prevNode, depth + 1, root, expandedNodes);

  return {
    nodeId: id,
    anchorId: root.getId(anchor),
    position: 'after',
  };
};

export const getPrevAnchoredPosition = <K, N>(
  id: ID,
  expandedNodes: ExpandedNodes,
  root: TreeViewRoot<K, N>
): null | SortInfo => {
  const list = nodeList(expandedNodes, root);
  const treeNode = findTreeNode(root, id, expandedNodes);
  if (!treeNode) {
    throw new Error(`Could not find node with id ${id}`);
  }
  const node: N = treeNode[0];

  // @ts-expect-error - TS2345 - Argument of type '([n]: [any]) => boolean' is not assignable to parameter of type '(value: TreeViewNode<K, N>, index: number, obj: TreeViewNode<K, N>[]) => unknown'.
  const index = list.findIndex(([n]: [any]) => n === node);
  // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
  const depth = nodeDepth(expandedNodes)(root)(node);
  const nextTreeNode = list[index + 1];
  const nextNode = nextTreeNode && nextTreeNode[0];

  // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
  if (nextNode && nodeDepth(expandedNodes)(root)(nextNode) >= depth) {
    return null;
  }

  const parentId = root.getId(
    // @ts-expect-error `getParent` could produce `null`
    getParent(root, id, expandedNodes)[0]
  );

  // Don’t allow going outside of root.
  if (
    !parentId ||
    // @ts-expect-error - TS2345 - Argument of type '([rootNode]: [any]) => boolean' is not assignable to parameter of type '(value: TreeViewNode<K, N>, index: number, array: TreeViewNode<K, N>[]) => unknown'.
    root.nodes.some(([rootNode]: [any]) => parentId === root.getId(rootNode))
  ) {
    return null;
  }

  return {
    nodeId: id,
    anchorId: parentId,
    position: 'after',
  };
};

// expandParentAnchor :: AnchoredPosition -> ExpandedNodes -> ExpandedNodes
const expandParentAnchor = ({
  anchorId,
  position,
}: SortInfo): ((arg1: ExpandedNodes) => ExpandedNodes) =>
  position === 'prepend' || position === 'append'
    ? assoc(anchorId)(true)
    : identity;

export const hasChildren = <K, N>(children: TreeViewChildren<K, N>): boolean =>
  children.some(([, nested]: [any, any]) => nested.length);

const hasMoreThanOneChild = <K, N>(
  children: TreeViewChildren<K, N>
): boolean => {
  let size = 0;
  for (const [, nested] of children) {
    size += nested.length;
    if (size > 1) {
      return true;
    }
  }

  return false;
};

const collapseEmptyParent = weakMemo(
  <K, N>(
    expandedNodes: ExpandedNodes
  ): ((arg1: SortInfo) => (arg1: TreeViewRoot<K, N>) => <T>(arg1: T) => T) =>
    weakMemo((sort: SortInfo) =>
      weakMemo((root: TreeViewRoot<K, N>): (<T>(value: T) => T) => {
        const [parent, lazyChildren] = getParent(
          moveNode<K, N>(expandedNodes)(sort)(root),
          sort.nodeId,
          expandedNodes
        )!;
        const children = force(lazyChildren);
        return hasMoreThanOneChild(children)
          ? identity
          : (assoc(root.getId(parent))(false) as any);
      })
    )
);

export const getInitialState = <K, N>(): Omit<TreeViewState<K, N>, 'root'> => ({
  width: 320,
  height: 480,
  nodeHeight: 24,
  expandedNodes: {},
  selectedNodes: IDSet.from([]),
  preselectedNode: null,
  sorting: false,
  sort: null,
  potentialParent: null,
  invalidSort: false,
  scrollView: {
    scrollTop: 0,
    scrollLeft: 0,
  },
});

// Not a UI selector:
// eslint-disable-next-line webflow/selector-memoization
export const selectNode = <K, N>(
  state: TreeViewState<K, N>,
  id: ID
): TreeViewState<K, N> => {
  return {...state, ...{selectedNodes: IDSet.from([id])}};
};

export const preselectNode = <K, N>(
  state: TreeViewState<K, N>,
  id: ID
): TreeViewState<K, N> => {
  return {...state, preselectedNode: id};
};

export const unpreselectNode = <K, N>(
  state: TreeViewState<K, N>
): TreeViewState<K, N> => {
  return {...state, preselectedNode: null};
};

export const expandNode = <K, N>(
  state: TreeViewState<K, N>,
  id: ID
): TreeViewState<K, N> => {
  const {expandedNodes} = state;

  if (expandedNodes[id]) {
    return state;
  }

  return {
    ...state,
    expandedNodes: {...expandedNodes, [id]: true},
  };
};

export const toggleNode = <K, N>(
  state: TreeViewState<K, N>,
  id: ID
): TreeViewState<K, N> => {
  const expandedNodes = state.expandedNodes;
  return {
    ...state,
    expandedNodes: {...expandedNodes, [id]: !expandedNodes[id]},
  };
};

export const startSort = <K, N>(
  state: TreeViewState<K, N>,
  sort: SortInfo
): TreeViewState<K, N> => {
  return {
    ...state,
    sorting: true,
    sort,
    expandedNodes: {...state.expandedNodes, [sort.nodeId]: false},
  };
};

export const changeSort = <K, N>(
  state: TreeViewState<K, N>,
  sort: SortInfo
): TreeViewState<K, N> => {
  return {
    ...state,
    sort,
    expandedNodes: pipe([
      collapseEmptyParent(state.expandedNodes)(
        // @ts-expect-error - `state.sort` may be nullish
        state.sort
        // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
      )(state.root),
      // Order here matters because anchor could be the old parent.
      expandParentAnchor(sort),
    ])(state.expandedNodes) as ExpandedNodes,
    potentialParent: null,
    invalidSort: false,
  };
};

export const endSort = <K, N>(
  state: TreeViewState<K, N>,
  sort: SortInfo
): TreeViewState<K, N> => {
  return {
    ...state,
    // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
    root: moveNode(state.expandedNodes)(sort)(state.root),
    sorting: false,
    sort: null,
    potentialParent: null,
    invalidSort: false,
  };
};

export const flagPotentialParent = <K, N>(
  state: TreeViewState<K, N>,
  nodeId: null | ID
): TreeViewState<K, N> => {
  return assoc('potentialParent')(nodeId)(state);
};

export const scroll = <K, N>(
  state: TreeViewState<K, N>,
  scrollData: ScrollViewData
): TreeViewState<K, N> => {
  return {
    ...state,
    scrollView: {
      scrollTop: scrollData.hasOwnProperty('scrollTop')
        ? scrollData.scrollTop
        : state.scrollView.scrollTop,
      scrollLeft: scrollData.hasOwnProperty('scrollLeft')
        ? scrollData.scrollLeft
        : state.scrollView.scrollLeft,
    },
  };
};

export const clampScrollTop = <K, N>(
  state: TreeViewState<K, N>
): TreeViewState<K, N> => {
  const {scrollTop} = lazyListClampScrollTop({
    height: state.height,
    rowHeight: state.nodeHeight,
    rowCount: nodeList(state.expandedNodes, state.root).length,
    scrollTop: state.scrollView.scrollTop,
  });

  if (scrollTop === state.scrollView.scrollTop) {
    return state;
  }

  return {
    ...state,
    scrollView: {...state.scrollView, scrollTop},
  };
};

const setScrollLeft = <K, N>(
  state: TreeViewState<K, N>,
  scrollLeft: number
): TreeViewState<K, N> =>
  state.scrollView.scrollLeft === scrollLeft
    ? state
    : {
        ...state,
        scrollView: {...state.scrollView, scrollLeft},
      };

export const clampScrollLeft = <K, N>(
  state: TreeViewState<K, N>
): TreeViewState<K, N> =>
  setScrollLeft(
    state,
    // TreeViewState has no `nodeWidth` but we use it here. Does this belong in
    // `NavigatorUtils` or `treePane`?
    //
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error ☝️
    clamp(state.scrollView.scrollLeft, 0, state.nodeWidth - state.width)
  );

export const bringSelectedNodeInView = <K, N>(
  state: TreeViewState<K, N>
): TreeViewState<K, N> => {
  const {sorting, sort: sort_} = state;
  // @ts-expect-error - TS2322 - Type 'SortInfo | null | undefined' is not assignable to type 'SortInfo'.
  const sort: SortInfo = sort_;
  const root = sorting
    ? // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
      moveNode(state.expandedNodes)(sort)(state.root)
    : state.root;
  // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<unknown, unknown> | TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
  const list = nodeList(state.expandedNodes, root);

  const singleSelectedNodeId =
    state.selectedNodes.size === 1 ? Array.from(state.selectedNodes)[0] : null;
  const idToBringIntoView = sorting ? sort.nodeId : singleSelectedNodeId;
  if (!idToBringIntoView) {
    return state;
  }

  // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<unknown, unknown> | TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
  const treeNode = findTreeNode(root, idToBringIntoView, state.expandedNodes);

  if (!treeNode) {
    return state;
  }

  const [node] = treeNode;
  // @ts-expect-error - TS2345 - Argument of type '([n]: [any]) => boolean' is not assignable to parameter of type '(value: TreeViewNode<unknown, unknown>, index: number, obj: TreeViewNode<unknown, unknown>[]) => unknown'.
  const index = list.findIndex(([n]: [any]) => n === node);

  const scrollTop = bringIndexInView(
    {
      rowHeight: state.nodeHeight,
      rowCount: list.length,
      height: state.height,
      scrollTop: state.scrollView.scrollTop,
    },
    index
  ).scrollTop;

  if (scrollTop === state.scrollView.scrollTop) {
    return state;
  }

  return {
    ...state,
    scrollView: {...state.scrollView, scrollTop},
  };
};

export const forTestSuite = {
  getNodeInfo,
  getPathToChildren,
  getIn,
  removeIn,
  flatten_,
  debugTree,
  deleteAt,
  set,
  insertAt,
  updateAt,
} as const;
